import { useContext } from 'react';
import ReactDOMServer from 'react-dom/server';
import {
    BreadcrumbList,
    FAQPage,
    HowTo,
    ImageObject,
    Organization,
    Product,
    Review,
    Thing,
    WebPage,
    WithContext,
} from 'schema-dts';
import { sanitizeData } from '../../lib/sanitizeData';
import { SchemaContext } from './SchemaContext';

export interface SchemaProps<T extends Thing> {
    data: WithContext<T>;
}

export interface BreadCrumb {
    label: string;
    path?: string;
}

export interface ItemData {
    id?: string;
    title: string;
    render: () => React.ReactElement;
    messages?: string[];
    keywords?: string[];
}

function Schema<T extends Thing>({ data }: SchemaProps<T>) {
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
                __html: JSON.stringify(sanitizeData(data)),
            }}
        />
    );
}

interface BreadCrumbsSchemaProps {
    breadcrumbs: BreadCrumb[];
}

export function BreadCrumbsSchema({ breadcrumbs }: BreadCrumbsSchemaProps) {
    const { site } = useContext(SchemaContext);

    return (
        <Schema<BreadcrumbList>
            data={{
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbs.map((breadcrumb: BreadCrumb, index: number) => ({
                    '@type': 'ListItem',
                    position: index,
                    name: breadcrumb.label,
                    item: breadcrumb.path && breadCrumbUrl(breadcrumb.path, site),
                })),
            }}
        />
    );
}

function breadCrumbUrl(path: string, site: string) {
    return path == '/' ? `https://${site}` : `https://${site}${path}`;
}

interface FAQSchemaProps {
    faqs: ItemData[];
}

export function FAQSchema({ faqs }: FAQSchemaProps) {
    return (
        <Schema<FAQPage>
            data={{
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: faqs.map((item: ItemData) => ({
                    '@type': 'Question',
                    name: item.title,
                    keywords: item.keywords,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: ReactDOMServer.renderToStaticMarkup(item.render()),
                    },
                })),
            }}
        />
    );
}

interface DesignCardSchemaProps {
    url: string;
    width?: number;
    height?: number;
    name?: string;
}

export function DesignCardSchema({ url, width, height, name }: DesignCardSchemaProps) {
    return (
        <Schema<ImageObject>
            data={{
                '@context': 'https://schema.org',
                '@type': 'ImageObject',
                url: url,
                width: width ? `${width}px` : '',
                height: height ? `${height}px` : '',
                name: name ? name : '',
                sourceOrganization: '99designs',
            }}
        />
    );
}

interface OrganizationSchemaProps {
    lang: string;
    supportPhone: string;
    sameAs: string[];
}

export function OrganizationSchema({ lang, supportPhone, sameAs }: OrganizationSchemaProps) {
    const { site } = useContext(SchemaContext);

    return (
        <Schema<Organization>
            data={{
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: '99designs',
                url: `https://${site}`,
                logo: `https://${site}/og-image.png`,
                contactPoint: {
                    '@type': 'ContactPoint',
                    telephone: supportPhone,
                    contactType: 'customer service',
                },
                sameAs: sameAs,
                subOrganization: {
                    '@type': 'Organization',
                    name: '99designs Support',
                    url: `https://support.${site}/hc/${lang}`,
                },
            }}
        />
    );
}

interface ProductSchemaProps {
    url: string;
    name: string;
    description: string;
    ratingValue?: string;
    reviewCount?: number;
}

export function ProductSchema({
    url,
    name,
    description,
    ratingValue,
    reviewCount,
}: ProductSchemaProps) {
    const schemaData: WithContext<Product> = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': url,
        name: name,
        description: description,
        brand: '99designs',
    };

    if (ratingValue && reviewCount) {
        schemaData.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: ratingValue,
            reviewCount: reviewCount,
        };
    }

    return <Schema<Product> data={schemaData} />;
}

export interface CategoryProductSchemaProps {
    categoryKey: string;
    productName: string;
    description: string;
    ratingValue?: string;
    reviewCount?: number;
}

export function CategoryProductSchema({
    categoryKey,
    productName,
    description,
    ratingValue,
    reviewCount,
}: CategoryProductSchemaProps) {
    const { site } = useContext(SchemaContext);

    return (
        <ProductSchema
            url={`https://${site}/${categoryKey}`}
            name={productName}
            description={description}
            ratingValue={ratingValue}
            reviewCount={reviewCount}
        />
    );
}

interface ReviewSchemaProps {
    review: string;
    ratingValue: number;
    author: string;
    categoryKey: string;
}

export function ReviewSchema({ review, ratingValue, author, categoryKey }: ReviewSchemaProps) {
    const { site } = useContext(SchemaContext);

    return (
        <Schema<Review>
            data={{
                '@context': 'https://schema.org',
                '@type': 'Review',
                reviewBody: review,
                itemReviewed: {
                    '@type': 'Product',
                    '@id': `https://${site}/${categoryKey}`,
                },
                reviewRating: {
                    '@type': 'Rating',
                    ratingValue: ratingValue,
                    bestRating: '5',
                    worstRating: '0',
                },
                author: { '@type': 'Person', name: author },
                publisher: {
                    '@type': 'Organization',
                    name: '99designs',
                    sameAs: 'https://99designs.com',
                },
            }}
        />
    );
}

interface WebPageSchemaProps {
    name: string;
    description: string;
    inLanguage: string;
    mainEntityOfPage: string;
    relatedLink: string[] | string;
}

export function WebPageSchema({
    name,
    description,
    inLanguage,
    mainEntityOfPage,
    relatedLink,
}: WebPageSchemaProps) {
    return (
        <Schema<WebPage>
            data={{
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                name,
                description,
                inLanguage,
                mainEntityOfPage,
                relatedLink,
            }}
        />
    );
}

export interface HowToStep {
    name: string;
    description: string;
}
export interface HowToSchemaProps {
    name: string;
    steps: HowToStep[];
}

export function HowToSchema({ name, steps }: HowToSchemaProps) {
    return (
        <Schema<HowTo>
            data={{
                '@context': 'https://schema.org',
                '@type': 'HowTo',
                name: name,
                step: steps.map((step: HowToStep, index: number) => ({
                    '@type': 'HowToStep',
                    position: index + 1,
                    name: step.name,
                    itemListElement: {
                        '@type': 'HowToDirection',
                        text: step.description,
                    },
                })),
            }}
        />
    );
}
